import React, { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { Link } from 'react-router-dom';
import { FaNewspaper, FaUsers, FaTags, FaFire, FaArrowRight } from 'react-icons/fa';
import PaperCollection from './PaperCollection';
import PaperSearchHero from './PaperSearchHero';
import { statsApi } from '../utils/apiUtils';
import { SOURCE_NAME } from '../constants/tagConstants';

const stagger = {
  animate: { transition: { staggerChildren: 0.08, delayChildren: 0.2 } }
};
const fadeUp = {
  initial: { opacity: 0, y: 20 },
  animate: { opacity: 1, y: 0, transition: { duration: 0.5, ease: [0.25, 0.1, 0.25, 1] } }
};

function PaperDigest() {
  const [stats, setStats] = useState({
    totalPapers: 0,
    totalUsers: 0,
    totalTopics: { topics: 0, subtopics: 0 }
  });
  const [hotPapers, setHotPapers] = useState([]);

  useEffect(() => {
    const fetchData = async () => {
      try {
        const [statsData, hotPapersData] = await Promise.all([
          statsApi.getGeneralStats(),
          statsApi.getHotPapers()
        ]);
        setStats(statsData);
        setHotPapers(hotPapersData);
      } catch (error) {
        console.error('Failed to fetch data:', error);
      }
    };
    fetchData();
  }, []);

  const statisticsItems = [
    { icon: <FaNewspaper />, label: "Papers", value: stats.totalPapers.toLocaleString() },
    { icon: <FaUsers />, label: "Users", value: stats.totalUsers.toLocaleString() },
    { icon: <FaTags />, label: "Topics", value: stats.totalTopics.topics.toLocaleString() },
    { icon: <FaTags />, label: "Subtopics", value: stats.totalTopics.subtopics.toLocaleString() },
  ];

  const importAll = (r) => r.keys().map(r);
  const logos = importAll(require.context('/static/media', false, /\.png$/));
  const duplicatedLogos = [...logos, ...logos];

  return (
    <div className="max-w-5xl mx-auto py-8 sm:py-20">
      {/* Hero */}
      <motion.div
        className="text-center mb-20"
        variants={stagger}
        initial="initial"
        animate="animate"
      >
        <motion.div variants={fadeUp} className="mb-6">
          <span className="inline-block text-xs text-yellow-300/70 uppercase tracking-[0.25em] font-semibold mb-4">
            // research companion
          </span>
        </motion.div>
        <motion.h1
          variants={fadeUp}
          className="text-5xl sm:text-7xl font-bold text-white mb-6 tracking-tighter leading-[0.9]"
        >
          Paper<br />
          <span className="text-yellow-300">Digest</span>
        </motion.h1>
        <motion.p
          variants={fadeUp}
          className="text-base sm:text-lg text-white/50 max-w-lg mx-auto leading-relaxed"
        >
          AI-powered summaries of the latest {SOURCE_NAME} papers,
          delivered daily to your research workflow.
        </motion.p>
      </motion.div>

      {/* Find-or-add hero — first-class surface for the dual-mode search
          that the cmd+k shortcut also reveals. */}
      <PaperSearchHero />

      {/* Stats — inline monospace style */}
      <motion.div
        className="flex flex-col sm:flex-row items-stretch gap-3 mb-20"
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ delay: 0.5 }}
      >
        {statisticsItems.map((item, index) => (
          <motion.div
            key={index}
            className="flex-1 glass-card rounded-lg p-4 sm:p-5 group hover:bg-white/10 transition-colors duration-300"
            initial={{ opacity: 0, y: 12 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.55 + index * 0.08 }}
          >
            <div className="flex items-center gap-3">
              <div className="text-yellow-300/70 text-sm group-hover:text-yellow-300 transition-colors">
                {item.icon}
              </div>
              <div className="min-w-0">
                <div className="text-[10px] text-white/30 uppercase tracking-widest font-semibold">{item.label}</div>
                <div className="text-white font-bold text-lg tabular-nums tracking-tight">{item.value}</div>
              </div>
            </div>
          </motion.div>
        ))}
      </motion.div>

      {/* Hot Papers */}
      <motion.div
        className="mb-20"
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ delay: 0.7 }}
      >
        <div className="flex items-center gap-3 mb-8">
          <FaFire className="text-yellow-300 text-sm" />
          <h2 className="text-lg sm:text-xl font-bold text-white uppercase tracking-wider">
            Hot Papers
          </h2>
          <div className="flex-1 h-px bg-white/10" />
        </div>
        <PaperCollection papers={hotPapers} />
      </motion.div>

      {/* Logo scroll */}
      <motion.div
        className="mb-20"
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ delay: 0.85 }}
      >
        <p className="text-center text-[10px] uppercase tracking-[0.3em] text-white/25 font-semibold mb-6">
          Trusted by researchers at
        </p>
        <div className="relative overflow-hidden w-full h-16">
          <div className="absolute inset-y-0 left-0 w-20 bg-gradient-to-r from-purple-600 to-transparent z-10 pointer-events-none" />
          <div className="absolute inset-y-0 right-0 w-20 bg-gradient-to-l from-red-500 to-transparent z-10 pointer-events-none" />
          <div className="flex absolute animate-scroll-slow">
            {duplicatedLogos.map((logo, index) => (
              <div key={index} className="flex items-center justify-center mx-8 min-w-[120px]">
                <img
                  src={logo}
                  alt={`University logo ${index + 1}`}
                  className="h-8 object-contain opacity-40 hover:opacity-70 transition-opacity duration-300 brightness-0 invert"
                />
              </div>
            ))}
          </div>
        </div>
      </motion.div>

      {/* CTA */}
      <motion.div
        className="text-center"
        initial={{ opacity: 0, y: 10 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ delay: 1 }}
      >
        <Link to="/today">
          <motion.button
            className="group inline-flex items-center gap-3 bg-yellow-300 text-gray-900 px-8 py-3.5 rounded-lg text-sm font-bold uppercase tracking-wider hover:bg-yellow-200 transition-all duration-200 shadow-glow-yellow"
            whileHover={{ scale: 1.03, y: -2 }}
            whileTap={{ scale: 0.97 }}
          >
            Explore Today's Papers
            <FaArrowRight className="text-xs group-hover:translate-x-0.5 transition-transform" />
          </motion.button>
        </Link>
      </motion.div>
    </div>
  );
}

export default PaperDigest;
